import React, { useState, useEffect } from 'react';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { staffAPI, tenantUsersAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import ContentHeader from '../components/ContentHeader';
import './ResetUserPassword.css';

function ResetUserPassword() {
  useTenantFromUrl();
  const [selectedUsername, setSelectedUsername] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const canReset = hasPermission('settings.manage_staff');

  useEffect(() => {
    if (!canReset) return;
    (async () => {
      try {
        setUsersLoading(true);
        const res = await tenantUsersAPI.getList({ exclude_developers: 'true' });
        setUsers(res.data?.data || []);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to load users');
        setUsers([]);
      } finally {
        setUsersLoading(false);
      }
    })();
  }, [canReset]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!selectedUsername.trim()) {
      setError('Select a user');
      return;
    }
    setLoading(true);
    try {
      const response = await staffAPI.forcePasswordReset(selectedUsername.trim());
      setSuccess(response.data.message || 'Password cleared. User must set a new password from the login page.');
      setSelectedUsername('');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!canReset) {
    return (
      <div className="reset-user-password">
        <ContentHeader title="Force password reset" />
        <p>You need the &quot;Manage Staff&quot; permission to force a password reset for another user.</p>
      </div>
    );
  }

  return (
    <div className="reset-user-password">
      <ContentHeader
        title="Force password reset"
        titleHint={'Select a user in this tenant. Their password will be cleared. They will need to use "Set initial password" on the login page to set a new password. Their role and access are unchanged.'}
      />
      <form onSubmit={handleSubmit} className="reset-user-password-form">
        {error && <div className="reset-user-password-error">{error}</div>}
        {success && <div className="reset-user-password-success">{success}</div>}
        <div className="form-group">
          <label htmlFor="user-select">User</label>
          <select
            id="user-select"
            value={selectedUsername}
            onChange={(e) => { setSelectedUsername(e.target.value); setError(''); setSuccess(''); }}
            disabled={loading || usersLoading}
            className="reset-user-password-select"
          >
            <option value="">— Select a user —</option>
            {users.map((u) => (
              <option key={u.id} value={u.username || u.email}>
                {u.staff_name ? `${u.staff_name} (${u.email || u.username})` : (u.email || u.username)}
              </option>
            ))}
          </select>
          {!usersLoading && users.length === 0 && (
            <p className="reset-user-password-no-users">No users in this tenant.</p>
          )}
        </div>
        <button type="submit" className="btn-submit" disabled={loading || usersLoading || !selectedUsername.trim()}>
          {loading ? 'Resetting...' : 'Force password reset'}
        </button>
      </form>
    </div>
  );
}

export default ResetUserPassword;
